import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Row, Col, Label } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import toast from "react-hot-toast";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const PaymentDayBook = () => {
  const [loading, setLoading] = useState(false);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    totalExpenses: 0,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchPaymentReport = async (start, end) => {
    setLoading(true);
    try {
      const response = await PaymentReportService.daybookList(start, end);
      setSummary({
        totalEarnings: response.totalEarnings,
        totalExpenses: response.totalExpenses,
      });
      setPaymentTypeList(response.paymentTypeEarnings || []);
    } catch (error) {
      console.error("Error fetching payment day book:", error);
      toast.error("Failed to fetch payment report");
    } finally {
      setLoading(false);
    }
  };

  const handleReportClick = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const formatDate = (date) => {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    fetchPaymentReport(formatDate(startDate), formatDate(endDate));
  };

  useEffect(() => {
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 1);

    setStartDate(oneMonthBefore);
    setEndDate(today);

    const formatDate = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    fetchPaymentReport(formatDate(oneMonthBefore), formatDate(today));
  }, []);

  const filteredList = paymentTypeList;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Day Book" pageTitle="Payment Reports" />

        {/* Filter Card (Desktop & Mobile) */}
        <Card className="mb-3 shadow-sm">
          <CardBody>
            <Row className="align-items-end g-2">
              <Col xs={6} lg={3}>
                <Label className="mb-1">Start Date</Label>
                <Flatpickr
                  value={startDate}
                  onChange={([date]) => setStartDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    maxDate: new Date(),
                  }}
                  className="form-control form-control-sm"
                  style={{ height: "42px" }}
                />
              </Col>
              <Col xs={6} lg={3}>
                <Label className="mb-1">End Date</Label>
                <Flatpickr
                  value={endDate}
                  onChange={([date]) => setEndDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    minDate: startDate,
                    maxDate: new Date(),
                  }}
                  className="form-control form-control-sm"
                  style={{ height: "42px" }}
                />
              </Col>
              <Col xs={12} lg={6} className="text-end mt-2 mt-lg-0">
                <Button
                  color="success"
                  className="me-2"
                  onClick={handleReportClick}
                  disabled={loading}
                >
                  Generate Report
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Desktop Table */}
        <Card className="mb-3 shadow-sm">
          <CardBody>
            <Row className="align-items-end g-2">
              <div className="d-lg-block d-none">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "80px" }}>S.N</th>
                        <th>Payment Type</th>
                        <th className="text-end">Amount (Rs.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredList.map((item, index) => (
                        <tr key={item.paymentTypeId}>
                          <td>{index + 1}</td>
                          <td>{item.paymentTypeName}</td>
                          <td className="text-end">{item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="table-success fw-bold">
                        <td colSpan={2}>Total Earnings</td>
                        <td className="text-end">
                          {summary.totalEarnings.toFixed(2)}
                        </td>
                      </tr>
                      <tr className="table-danger fw-bold">
                        <td colSpan={2}>Total Expenses</td>
                        <td className="text-end">
                          {summary.totalExpenses.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Row>
          </CardBody>
        </Card>

        {/* Mobile Cards */}
        <div className="d-block d-lg-none">
          {loading ? (
            <div className="d-flex justify-content-center my-4">
              <Triangle visible={true} height="60" width="60" color="#5B71B9" />
            </div>
          ) : filteredList.length > 0 ? (
            filteredList.map((item, index) => (
              <Card key={item.paymentTypeId} className="mb-2 shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{item.paymentTypeName}</strong>
                    <div className="text-muted small mt-1">
                      S.N: {index + 1}
                    </div>
                  </div>
                  <div className="fw-bold">Rs. {item.amount.toFixed(2)}</div>
                </CardBody>
              </Card>
            ))
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-0">No payment records found.</p>
            </div>
          )}

          {/* Totals */}
          <Card className="mb-2 shadow-sm bg-success text-white">
            <CardBody className="d-flex justify-content-between">
              <span>Total Earnings</span>
              <span>Rs. {summary.totalEarnings.toFixed(2)}</span>
            </CardBody>
          </Card>
          <Card className="mb-2 shadow-sm bg-danger text-white">
            <CardBody className="d-flex justify-content-between">
              <span>Total Expenses</span>
              <span>Rs. {summary.totalExpenses.toFixed(2)}</span>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentDayBook;
